import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { Box, Dialog, Typography, IconButton } from "@mui/material";
import { X, Info } from "lucide-react";
import { useDispatch } from "react-redux";
import AppButton from "../../../common_components/AppButton";
import { Dropdown } from "../../../common_components/FormInputs";
import { toast } from "../../../common_components/Toast/ToastUtils";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import { bulkImportAffiliateClicksThunk } from "../../../redux/affiliateClickImportSlice";
import { useAffiliateImportOptions, formatFileSize } from "../shared";
// General import instructions shown in the header (i) tooltip.
const IMPORT_INSTRUCTIONS = (_jsxs(Box, { sx: { maxWidth: 340, color: "#fff" }, children: [_jsx(Typography, { sx: { fontSize: "13px", fontWeight: 600, mb: 0.5 }, children: "How to import" }), _jsxs(Typography, { sx: { fontSize: "12px", mb: 1 }, children: ["Pick a ", _jsx("b", { children: "Network" }), " and upload the report file. If the sheet includes the advertiser/client column, the client is matched automatically \u2014 otherwise also pick a ", _jsx("b", { children: "Client" }), " and", " ", _jsx("b", { children: "Account" }), "."] })] }));
const ImportClicksModal = ({ open, onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const [clientId, setClientId] = useState("");
    const [network, setNetwork] = useState("");
    const [connectionId, setConnectionId] = useState("");
    const [file, setFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileRef = useRef(null);
    const { clientOptions, clientsLoading, networkOptions, connectionOptions, connectionsLoading } = useAffiliateImportOptions(clientId, open, network);
    // Reset the form whenever the popup opens/closes.
    useEffect(() => {
        if (!open) {
            setClientId("");
            setNetwork("");
            setConnectionId("");
            setFile(null);
            setIsSubmitting(false);
        }
    }, [open]);
    // Keep the account selection consistent with the chosen client + network. While
    // the connections are still loading we leave the value alone.
    useEffect(() => {
        if (!clientId || !network) {
            setConnectionId("");
            return;
        }
        if (connectionOptions.length === 0)
            return;
        if (connectionOptions.length === 1) {
            setConnectionId(connectionOptions[0].value);
        }
        else if (!connectionOptions.some((option) => option.value === connectionId)) {
            setConnectionId("");
        }
    }, [clientId, network, connectionOptions, connectionId]);
    // Import only needs a Network + File. Client + account are optional (only for
    // sheets without an advertiser column) — so picking a Client must NOT block the
    // button; it's validated as a soft prompt on submit instead.
    const requireAccount = Boolean(clientId && network && connectionOptions.length > 0);
    const canSubmit = Boolean(network && file && !isSubmitting);
    const openFilePicker = () => fileRef.current?.click();
    const handleFileSelect = (e) => {
        const selected = Array.from(e.target.files || [])[0];
        if (selected) {
            const isValid = [".csv", ".xlsx", ".xls"].some((ext) => selected.name.toLowerCase().endsWith(ext));
            if (!isValid) {
                toast.error(`File ${selected.name} is not a valid CSV/Excel file.`);
            }
            else {
                setFile(selected);
            }
        }
        if (fileRef.current)
            fileRef.current.value = "";
    };
    const removeFile = () => setFile(null);
    const handleSubmit = async () => {
        if (!network)
            return toast.error("Please select a network.");
        if (requireAccount && !connectionId)
            return toast.error("Please select an account name.");
        if (!file)
            return toast.error("Please select a CSV file to import.");
        try {
            setIsSubmitting(true);
            const formData = new FormData();
            formData.append("file", file);
            formData.append("network", network);
            if (clientId)
                formData.append("client_id", clientId);
            if (connectionId)
                formData.append("connection_id", connectionId);
            const res = await dispatch(bulkImportAffiliateClicksThunk(formData)).unwrap();
            const summary = res?.data;
            // Publisher pipelines (CJ, Impact) return a processing summary.
            if (summary && typeof summary.processed_rows === "number") {
                if (summary.processed_rows === 0) {
                    toast.error(summary.errors?.[0]?.reason || "No rows could be imported. Check the file and selection.");
                    return; // keep the popup open so the user can fix it
                }
                toast.success(`Imported ${summary.processed_rows} of ${summary.total_rows} rows` +
                    (summary.skipped_rows ? ` (${summary.skipped_rows} skipped)` : "") + ".");
            }
            else {
                toast.success("File imported successfully!");
            }
            onSuccess?.();
            onClose();
        }
        catch (error) {
            toast.error(typeof error === "string" ? error : "Failed to import clicks");
        }
        finally {
            setIsSubmitting(false);
        }
    };
    return (_jsxs(Dialog, { open: open, onClose: () => !isSubmitting && onClose(), maxWidth: "sm", fullWidth: true, PaperProps: { sx: { borderRadius: "12px" } }, children: [_jsxs(Box, { sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 1.5,
                    borderBottom: "1px solid #EAECF0",
                }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(IconButton, { size: "small", onClick: () => !isSubmitting && onClose(), "aria-label": "Close", children: _jsx(X, { size: 18, color: "#667085" }) }), _jsx(Typography, { sx: { fontSize: "16px", fontWeight: 600, color: "#101828" }, children: "Import Clicks" }), _jsx(HoverTooltip, { title: IMPORT_INSTRUCTIONS, placement: "bottom-start", children: _jsx(IconButton, { size: "small", sx: { p: 0.25 }, "aria-label": "Import instructions", children: _jsx(Info, { size: 16, color: "#98A2B3" }) }) })] }), _jsx(AppButton, { variant: "contained", color: "primary", className: "app-common-button", onClick: handleSubmit, loading: isSubmitting, disabled: !canSubmit, sx: { bgcolor: "#FF008E", "&:hover": { bgcolor: "#E6007A" } }, children: isSubmitting ? "Importing..." : "Import" })] }), _jsxs(Box, { sx: { p: 3, display: "flex", flexDirection: "column", gap: 2.5 }, children: [_jsx(Box, { sx: { maxWidth: { xs: "100%", sm: "50%" } }, children: _jsx(Dropdown, { label: "Network", required: true, value: network, options: networkOptions, onChange: (v) => {
                                setNetwork(v);
                                setConnectionId("");
                            }, placeholder: "Select network", fullWidth: true }) }), _jsx(Box, { children: _jsxs(Box, { sx: { display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }, children: [_jsx(Box, { sx: { flex: 1, minWidth: 0 }, children: _jsx(Dropdown, { label: "Client", value: clientId, options: clientOptions, onChange: (v) => {
                                            setClientId(v);
                                            setConnectionId("");
                                        }, placeholder: "Select client", searchable: true, loading: clientsLoading, fullWidth: true }) }), _jsx(Box, { sx: { flex: 1, minWidth: 0 }, children: _jsx(Dropdown, { label: "Account Name", required: requireAccount, value: connectionId, options: connectionOptions, onChange: (v) => setConnectionId(v), placeholder: !clientId
                                            ? "Select a client first"
                                            : !network
                                                ? "Select a network first"
                                                : connectionsLoading
                                                    ? "Loading account names..."
                                                    : connectionOptions.length === 0
                                                        ? "No account names for this network"
                                                        : "Select account name", searchable: true, disabled: !clientId || !network, loading: connectionsLoading, fullWidth: true }) })] }) }), _jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: "14px", fontWeight: 600, color: "#EE2A7B", mb: 1 }, children: "Upload File" }), _jsxs(Box, { onClick: !file ? openFilePicker : undefined, sx: {
                                    border: "1px dashed #D0D5DD",
                                    borderRadius: "8px",
                                    p: 4,
                                    textAlign: "center",
                                    cursor: file ? "not-allowed" : "pointer",
                                    bgcolor: file ? "#F9FAFB" : "transparent",
                                    opacity: file ? 0.7 : 1,
                                    "&:hover": { bgcolor: "#F9FAFB" },
                                }, children: [_jsx("input", { ref: fileRef, type: "file", onChange: handleFileSelect, style: { display: "none" }, accept: ".xlsx, .xls, .csv", disabled: !!file }), _jsx(Box, { sx: {
                                            width: 40,
                                            height: 40,
                                            borderRadius: "50%",
                                            bgcolor: "#F2F4F7",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mx: "auto",
                                            mb: 1.5,
                                        }, children: _jsx("img", { src: "/assets/upload.svg", alt: "upload", style: { width: 20, height: 20 } }) }), _jsx(Typography, { sx: { fontSize: "14px", color: "#475467" }, children: file ? ("File ready to import") : (_jsxs(_Fragment, { children: ["Drag and Drop or ", _jsx("span", { style: { color: "#EE2A7B", fontWeight: 600 }, children: "Browse" }), " to upload"] })) }), _jsx(Typography, { sx: { fontSize: "12px", color: "#667085", mt: 0.5 }, children: "Supported Format: CSV, Excel" })] }), file && (_jsxs(Box, { sx: {
                                    mt: 1.5,
                                    p: 1,
                                    border: "1px solid #EAECF0",
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5 }, children: [_jsx("img", { src: "/assets/xlicon.svg", alt: "" }), _jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: "13px", fontWeight: 500, color: "#344054" }, children: file.name }), _jsxs(Typography, { sx: { fontSize: "11px", color: "#667085" }, children: ["Size: ", formatFileSize(file.size)] })] })] }), _jsx(IconButton, { size: "small", onClick: removeFile, disabled: isSubmitting, children: _jsx("img", { src: "/assets/delete.svg", alt: "Delete", style: { width: 16 } }) })] }))] })] })] }));
};
export default ImportClicksModal;
